import memoizeOne from "memoize-one";
import { Component } from "react";

/**
 * Returns a string with its first letter in uppercase
 * @param {String} str
 * @returns {String}
 */
function capitalize(string_) {
  return string_ ? string_[0].toUpperCase() + string_.slice(1) : string_;
}
/**
 * Builds a name for a handler out of a field name:
 * getChangeHandlerName(password) => handlePasswordChange
 * @param {String} field
 * @returns {String}
 */
function buildChangeHandlerName(field) {
  return `handle${capitalize(field)}Change`;
}

/**
 * Builds a name for a handler out of a field name:
 * getBlurHandlerName(password) => handlePasswordBlur
 * @param {String} field
 * @returns {String}
 */
function buildBlurHandlerName(field) {
  return `handle${capitalize(field)}Blur`;
}

/**
 * Updates an object's values based on their current value
 * using the 'setter' function
 * @param {Function} setter - function to update the object
 * @param {Object} object - object to change
 */
function setProperties(setter, object) {
  return Object.entries(object).reduce(
    (accumulator, [key, value]) => ({
      ...accumulator,
      [key]: setter(value, key),
    }),
    object,
  );
}

/**
 * Supported field types
 */
export const FIELD_TYPES = /** @type {const} */ ({
  CHECKBOX: "checkbox",
  EMAIL: "email",
  NUMBER: "number",
  PASSWORD: "password",
  RADIO: "radio",
  TELEPHONE: "tel",
  TEXT: "text",
});

/**
 * @template ComponentProps -- Generic in JSDoc
 * @template FormFieldTypes -- Generic in JSDoc
 * @template InitialValues -- Generic in JSDoc
 * @param {React.FunctionComponent<ComponentProps>} WrappedComponent -- WrappedComponent need not have lifecycle methods, so we type as React.FC
 * @param {object} formDefinition
 * @param {FormFieldTypes} formDefinition.types - object containing {field: type} pairs
 * @param {Function} formDefinition.validate - function to validate the form
 * @param {InitialValues} [formDefinition.initialValues] - object of initial values from query string
 */
export default function stateForm(
  WrappedComponent,
  { initialValues = {}, types, validate },
) {
  const fields = Object.keys(types);
  const actualInitialValues = setProperties((type, field) => {
    if (type === FIELD_TYPES.CHECKBOX) {
      return false;
    }
    return initialValues[field] || "";
  }, types);
  const initialInvalid = {
    ...setProperties(() => {}, types),
    ...validate(initialValues),
  };

  class StateForm extends Component {
    state = {
      dirty: setProperties(() => false, types),
      invalid: initialInvalid,
      isLoading: false,
      isSubmitAttempted: false,
      pristine: setProperties(() => true, types),
      values: actualInitialValues,
    };

    // eslint-disable-next-line unicorn/consistent-function-scoping
    invalidChecker = memoizeOne((state) =>
      Object.values(state.invalid).some(Boolean),
    );

    constructor(properties) {
      super(properties);

      fields.forEach((field) => {
        const type = types[field];
        const changeHandlerName = buildChangeHandlerName(field);
        const blurHandlerName = buildBlurHandlerName(field);
        if (type === FIELD_TYPES.CHECKBOX) {
          this[changeHandlerName] = this.buildCheckboxChangeHandler(field);
        } else if (type === FIELD_TYPES.RADIO) {
          this[changeHandlerName] = this.buildRadioChangeHandler(field);
        } else {
          this[changeHandlerName] = this.buildTextChangeHandler(field);
          this[blurHandlerName] = this.buildTextBlurHandler(field);
        }
      });
    }

    componentDidMount() {
      this.isStateFormMounted = true;
    }

    componentWillUnmount() {
      this.isStateFormMounted = false;
    }

    get isFormInvalid() {
      return this.invalidChecker(this.state);
    }

    getFormFields = memoizeOne((state) => {
      const { values } = state;
      return Object.fromEntries(
        fields.map((field) => [
          field,
          {
            error: this.getFieldError(field),
            onBlur: this[buildBlurHandlerName(field)],
            onChange: this[buildChangeHandlerName(field)],
            value: values[field],
          },
        ]),
      );
    });

    validate = () => {
      this.setState((st) => ({
        invalid: validate(st.values),
      }));
    };

    getFieldError = (field) =>
      this.state.isSubmitAttempted || this.state.dirty[field]
        ? this.state.invalid[field]
        : undefined;

    buildCheckboxChangeHandler =
      (name) =>
      ({ target: { checked } }) =>
        this.setState(
          (st) => ({
            dirty: {
              ...st.dirty,
              [name]: true,
            },
            values: {
              ...st.values,
              [name]: checked,
            },
          }),
          () => this.validate(),
        );

    buildRadioChangeHandler =
      (name) =>
      ({ target: { value } }) => {
        this.setState(
          (st) => ({
            dirty: {
              ...st.dirty,
              [name]: true,
            },
            values: {
              ...st.values,
              [name]: value,
            },
          }),
          () => this.validate(),
        );
      };

    buildTextChangeHandler = (name) => (event) => {
      const {
        target: { value },
        type,
      } = event;
      event.persist();
      if (type !== "change") {
        return;
      }
      this.setState(
        (st) => ({
          pristine: {
            ...st.pristine,
            // This is used to track any edit made to a field
            [name]: false,
          },
          values: {
            ...st.values,
            [name]: value,
          },
        }),
        () => {
          // only validate once a field has already been edited and blur is triggered
          if (this.state.dirty[name]) {
            this.validate();
          }
        },
      );
    };

    buildTextBlurHandler = (name) => (event) => {
      const {
        target: { value },
        type,
      } = event;
      event.persist();
      if (type !== "blur") {
        return;
      }
      this.setState(
        (st) => ({
          dirty: {
            ...st.dirty,
            // set dirty to true once the field has been edited i.e. pristine is false
            [name]: !st.pristine[name],
          },
          values: {
            ...st.values,
            [name]: value,
          },
        }),
        () => this.validate(),
      );
    };

    buildSubmitHandler = (onSubmit) => (event) => {
      event.preventDefault();
      this.validate();
      this.setState({ isSubmitAttempted: true }, () => {
        // This block needs to be run on a setState callback because
        // forceValidation may have enqueued state changes that we are
        // otherwise unable to account for
        if (this.isFormInvalid) {
          return;
        }

        this.setState({ isLoading: true });
        onSubmit(this.state.values)
          .catch(() => {})
          .then(() => {
            if (this.isStateFormMounted) {
              this.setState({ isLoading: false });
            }
          });
      });
    };

    render() {
      const { isLoading } = this.state;
      const formFields = this.getFormFields(this.state);
      return (
        <WrappedComponent
          buildSubmitHandler={this.buildSubmitHandler}
          formFields={formFields}
          isLoading={isLoading}
          {...this.props}
        />
      );
    }
  }

  const displayName =
    WrappedComponent.displayName || WrappedComponent.name || "Component";
  StateForm.displayName = `stateForm(${displayName})`;

  return StateForm;
}
